"use client";
import { useEffect, useRef, useState } from "react";
import { githubHome, scopedGithubUrl } from "../../lib/github-scope";
export default function PortfolioGithubBrowser({ initialUrl }: { initialUrl: string }) {
  const [pages, setPages] = useState([initialUrl]);
  const [index, setIndex] = useState(0);
  const [address, setAddress] = useState(initialUrl);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const frame = useRef<HTMLIFrameElement>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const current = pages[index];
  const navigate = (value: string) => {
    const allowed = scopedGithubUrl(value, current);
    if (!allowed) { setError("Navigation blocked. Stay within github.com/SahiDemon and its repositories."); setAddress(current); return; }
    setError("");
    if (allowed === current) return;
    setPages(previous => [...previous.slice(0, index+1), allowed]);
    setIndex(index+1); setAddress(allowed); setLoading(true);
  };
  useEffect(() => {
    const allowed = scopedGithubUrl(initialUrl) ?? githubHome;
    setPages([allowed]); setIndex(0); setAddress(allowed); setLoading(true); setError("");
  }, [initialUrl]);
  useEffect(() => () => cleanup.current?.(), []);
  return <div className="app-content github-browser" tabIndex={0}>
    <form className="app-toolbar browser-toolbar" onSubmit={event => {event.preventDefault();navigate(address);}}>
      <button type="button" aria-label="Previous GitHub page" disabled={index===0} onClick={() => {setIndex(index-1);setAddress(pages[index-1]);setError("");setLoading(true);}}>←</button>
      <button type="button" aria-label="Next GitHub page" disabled={index===pages.length-1} onClick={() => {setIndex(index+1);setAddress(pages[index+1]);setError("");setLoading(true);}}>→</button>
      <button type="button" aria-label="GitHub home" onClick={() => navigate(githubHome)}>⌂</button>
      <input aria-label="GitHub address" value={address} spellCheck={false} onChange={event => setAddress(event.target.value)} />
      <button type="submit">Go</button>
    </form>
    {error && <div className="app-navigation-error" role="alert">{error}<button aria-label="Dismiss navigation message" onClick={() => setError("")}>×</button></div>}
    <div className="browser-status" role="status">{loading ? "Loading GitHub…" : "GitHub"}</div>
    <iframe ref={frame} title="SahiDemon GitHub browser" className="app-iframe" sandbox="allow-same-origin"
      referrerPolicy="no-referrer" src={`/api/github-view?url=${encodeURIComponent(current)}`}
      onLoad={() => {
        setLoading(false); cleanup.current?.();
        const doc = frame.current?.contentDocument; if (!doc) return;
        const click = (event: MouseEvent) => {
          const target = event.target;
          const anchor = target && (target as Node).nodeType === 1 ? (target as Element).closest("a") : (target as HTMLElement | null)?.parentElement?.closest("a");
          if (!anchor || anchor.getAttribute("href")?.startsWith("#")) return;
          event.preventDefault();
          const raw = anchor.getAttribute("data-github-url");
          const href = anchor.getAttribute("href");
          if (raw) navigate(raw);
          else if (href) { try {navigate(new URL(href, location.origin).searchParams.get("url") ?? href);} catch {setError("Navigation blocked.");} }
        };
        const key = (event: KeyboardEvent) => {
          if (event.key === "Escape" || (event.altKey && (event.key === "Tab" || event.code === "Backquote"))) {
            event.preventDefault(); frame.current?.parentElement?.dispatchEvent(new KeyboardEvent("keydown", {key:event.key,code:event.code,altKey:event.altKey,shiftKey:event.shiftKey,bubbles:true}));
          }
        };
        doc.addEventListener("click",click); doc.addEventListener("keydown",key);
        cleanup.current=()=>{doc.removeEventListener("click",click);doc.removeEventListener("keydown",key);};
      }} />
  </div>;
}
